import { createClient, type SupabaseClient } from '@supabase/supabase-js';
export interface SupabasePublicConfig {
  url: string;
  publishableKey: string;
  accessToken?: string;
  persistSession?: boolean;
}
/** Only publishable/anon keys belong here. A service-role key must never enter a browser. */
export function createGgbClient(config: SupabasePublicConfig): SupabaseClient {
  const url = new URL(config.url);
  if (url.protocol !== 'https:' && url.hostname !== 'localhost' && url.hostname !== '127.0.0.1')
    throw new Error('Supabase must use HTTPS.');
  if (!config.publishableKey) throw new Error('Supabase publishable key is required.');
  if (config.publishableKey.startsWith('sb_secret_'))
    throw new Error('Use a publishable key, never a secret key.');
  if (config.publishableKey.split('.').length === 3) {
    try {
      const payload = JSON.parse(
        atob(config.publishableKey.split('.')[1]!.replace(/-/g, '+').replace(/_/g, '/')),
      );
      if (payload.role === 'service_role') throw new Error('SECRET_KEY');
    } catch (error) {
      if (error instanceof Error && error.message === 'SECRET_KEY')
        throw new Error('Use an anon/publishable key, never a service-role key.');
    }
  }
  return createClient(config.url, config.publishableKey, {
    auth: {
      persistSession: config.persistSession ?? true,
      autoRefreshToken: !config.accessToken,
      detectSessionInUrl: !config.accessToken,
    },
    global: config.accessToken
      ? { headers: { Authorization: `Bearer ${config.accessToken}` } }
      : undefined,
  });
}
export class DataError extends Error {
  constructor(
    public readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = 'DataError';
  }
}
export function unwrap<T>(result: {
  data: unknown;
  error: { code?: string; message: string } | null;
}): T {
  if (result.error)
    throw new DataError(result.error.code ?? 'DATABASE_ERROR', result.error.message);
  return result.data as T;
}
